/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IDobbiesCheckoutCncDetailsData } from './dobbies-checkout-cnc-details.data';
import { IDobbiesCheckoutCncDetailsProps } from './dobbies-checkout-cnc-details.props.autogenerated';
import { getCheckoutState } from '@msdyn365-commerce/global-state';
import { CheckoutAccordionStateInput } from '../../themes/dobbies/actions/checkout-accordion-steps-state';

export interface IDobbiesCheckoutCncDetailsViewProps extends IDobbiesCheckoutCncDetailsProps<IDobbiesCheckoutCncDetailsData> {
    isBeingEdited?: boolean;
    fullName?: string;
    email?: string;
    phone?: string;
    onSaveDetails(email: string, fullName?: string, phoneNum?: string): void;
    onEdit(): void;
    onContinueToPayment(): void;
}

export interface IDobbiesCheckoutCNCState {
    isBeingEdited?: boolean;
    fullName?: string;
    email?: string;
    phone?: string;
}
/**
 *
 * DobbiesCheckoutCncDetails component
 * @extends {React.PureComponent<IDobbiesCheckoutCncDetailsProps<IDobbiesCheckoutCncDetailsData>>}
 */
class DobbiesCheckoutCncDetails extends React.PureComponent<
    IDobbiesCheckoutCncDetailsProps<IDobbiesCheckoutCncDetailsData>,
    IDobbiesCheckoutCNCState
> {
    constructor(props: IDobbiesCheckoutCncDetailsProps<IDobbiesCheckoutCncDetailsData>) {
        super(props);
        this.state = {};
    }

    public async componentDidMount(): Promise<void> {
        const checkoutState = await getCheckoutState(this.props.context.actionContext);
        if (this.props.context.request.user.isAuthenticated) {
            const emailAddress = this.props.data.customerInformation.result?.Email;
            if (!checkoutState.checkoutCart.cart.ReceiptEmail) {
                await checkoutState.checkoutCart.updateReceiptEmail({
                    newEmail: emailAddress || this.props.context.request.user.emailAddress || ''
                });
                this.setState({ isBeingEdited: false, email: emailAddress || this.props.context.request.user.emailAddress });
            } else {
                this.setState({ isBeingEdited: false, email: checkoutState.checkoutCart.cart.ReceiptEmail });
            }
            await checkoutState.updateGuestCheckoutEmail({
                newGuestCheckoutEmail: emailAddress || this.props.context.request.user.emailAddress || ''
            });
        } else {
            if (!checkoutState.checkoutCart.cart.ReceiptEmail) {
                this.setState({ isBeingEdited: true });
            } else {
                this.setState({ isBeingEdited: false, email: checkoutState.checkoutCart.cart.ReceiptEmail });
            }
        }
    }

    public render(): JSX.Element | null {
        const viewProps: IDobbiesCheckoutCncDetailsViewProps = {
            ...this.props,
            isBeingEdited: this.state.isBeingEdited,
            fullName: this.state.fullName,
            email: this.state.email,
            phone: this.state.phone,
            onSaveDetails: this.onSaveDetails,
            onEdit: this.onEdit,
            onContinueToPayment: this.onContinueToPayment
        };
        return this.props.renderView(viewProps);
    }
    private onSaveDetails = async (email: string, fullName?: string, phoneNum?: string) => {
        if (email) {
            const checkoutState = await getCheckoutState(this.props.context.actionContext);
            await checkoutState.checkoutCart.updateReceiptEmail({ newEmail: email });
            await checkoutState.updateGuestCheckoutEmail({ newGuestCheckoutEmail: email });
            this.setState({ isBeingEdited: false, email: email });
        }
    };

    private onContinueToPayment = () => {
        this.props.context.actionContext.update(new CheckoutAccordionStateInput(), { currentStep: 3 });
    };

    private onEdit = () => {
        this.setState({ isBeingEdited: true });
        this.props.context.actionContext.update(new CheckoutAccordionStateInput(), { currentStep: 2 });
    };
}

export default DobbiesCheckoutCncDetails;
